import { blinkConstants } from '@thoughtspot/blink-constants';
import { CLIENT_STATE_KEYS } from '@thoughtspot/client-state-util';
import { DEFAULT_PERCENTAGE_FORMAT, getColumnFormatPattern, getDataFormatter, getDecimals, getFormatPattern, getFormattingFromColumn, getLocale, hasUnit, isEffectivelyPercent, NULL_SUMMARY_MODE, setUnit, useMeasureOnXAxisAsOrdinal, } from '@thoughtspot/formatter-service';
import { getMinDifferentiatingPrecision } from '@thoughtspot/js-util';
import { DEFAULT_DECIMAL_PRECISION, getValueFormattedByConfig, } from '@thoughtspot/number-format-util';
import { getDefaultDataSizeForHighCardinalityCharts, getGeoDataBatchSize, getMaxDataSizeForHighCardinalityCharts, isDefaultNumberFormatUnitNone, } from '@thoughtspot/session-service';
import { getId, getSourceName, hasCustomCalendar, isAttribute, isCalendarGregorianOrFiscal, isColumnValidForNumberFormatting, isDateColumn, isDateFamilyColumn, isDoubleColumn, isEffectivelyNonNumeric, isEffectivelyNumeric, isGroupingColumn, isMeasure, isNumeric, isTimeColumn, supportsDateBucketizationChange, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { isShowAnomaliesEnabled, isShowAnomaliesSupported, isShowBoundsEnabled, } from '../components/kpi/cortex-utils';
import { isComparisionEnabled } from '../components/kpi/kpi-utils';
import { getSeries } from '../data/data-util';
import { getEnvFlags, getTranslatedTextCallback } from '../env';
import { AxisTypes, BackgroundFormatTypes, ChartResponsiveLayout, ChartType, ColumnSortConfigOptions, ConditionalFormattingComparisonTypes, HandleMissingValues, Unit, } from '../types';
import { createLogger } from '../utils/logger';
import { chartProviders, configOptions, doesSupportHighCardinalityData, getChartProvider, getQueryDefinitions, isGeoChartType, isMultiQueryChart, isStackedChart, shouldShowDataLabelToggleForColumn, shouldShowDetailLabelToggleForColumn, translateAxisConfigWithMeasureNamesValues, translateQueryDefinitionWithMeasureNamesValues, useMeasureOnYAxisAsOrdinal, useOrdinalXAxis, } from './chart-type-specification-service';
import { formattingConstants } from './chart-ui-utils';
import { getName } from './column-name-util';
import { getConditionalFormats } from './conditional-formatting-utils';
import { containsMeasureNamesOrMeasureValuesColumns, isMeasureNameOrValueColumn, } from './measure-names-and-values';
const logger = createLogger('chart-model-utils');
export const getColumns = (vizModel) => {
    if (vizModel.__typename === 'HeadlineViz') {
        return [vizModel.headlineColumn];
    }
    return _.map(vizModel.columns, col => col.column);
};
export const getColumn = (vizModel, id) => {
    if (vizModel.__typename === 'HeadlineViz') {
        return vizModel === null || vizModel === void 0 ? void 0 : vizModel.headlineColumn;
    }
    return getColumns(vizModel).find(col => getId(col) === id);
};
export const getSageColumns = (vizModel) => {
    const allColumns = getColumns(vizModel);
    return allColumns.filter(column => !isMeasureNameOrValueColumn(column));
};
const getEffectiveIdToColumnMap = (chartModel) => {
    const map = {};
    _.each(getColumns(chartModel), col => (map[getId(col)] = col));
    return map;
};
const getColumnsMap = (chartModel) => {
    const columns = getColumns(chartModel);
    const columnsMap = {};
    _.each(columns, column => {
        columnsMap[column.id] = column;
    });
    return columnsMap;
};
export const getTimeFamilyColumns = (chartModel) => {
    return _.filter(getColumns(chartModel), function (column) {
        return isDateFamilyColumn(column);
    });
};
export const getDistinctNamesForColumns = (columns) => {
    const columnNameToColumnCount = {};
    columns.forEach(column => {
        const columnName = getName(column);
        if (!columnNameToColumnCount.hasOwnProperty(columnName)) {
            columnNameToColumnCount[columnName] = 0;
        }
        columnNameToColumnCount[columnName]++;
    });
    return columns.map(column => {
        const columnName = getName(column);
        const columnCountWithSameName = columnNameToColumnCount[columnName];
        if (columnCountWithSameName === 1) {
            return columnName;
        }
        const sourceName = getSourceName(column, getTranslatedTextCallback());
        if (!sourceName) {
            return columnName;
        }
        return sourceName;
    });
};
export const getTimeSeriesColumns = (chartModel) => _.filter(getColumns(chartModel), col => isDateColumn(col));
export const getAttributeColumns = (chartModel) => _.filter(getColumns(chartModel), col => isEffectivelyNonNumeric(col) || isGroupingColumn(col));
export const getMeasureColumns = (chartModel) => getColumns(chartModel).filter(column => isEffectivelyNumeric(column) && !isGroupingColumn(column));
export const getSortOrderByColId = (viz, columnId) => {
    if (_.isEmpty(viz.sortInfo)) {
        logger.debug('Sort info is missing in the chart object');
        if (!_.isEmpty(viz.topInfo)) {
            return _.find(viz.topInfo, sortOrder => sortOrder.columnId === columnId);
        }
        const chartModel = viz;
        if (_.isNil(chartModel.sortOrder)) {
            logger.debug('Sort order is missing in the chart object');
            return null;
        }
        return _.find(chartModel.sortOrder, sortOrder => sortOrder.columnId === columnId);
    }
    return _.find(viz.sortInfo, sortOrder => sortOrder.columnId === columnId);
};
export const getChartType = (chartModel) => { var _a; return (_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.chartType; };
export const getClientState = (viz) => viz.vizProp;
export const getUserData = (model, key, defaultValue = {}) => _.get(model.vizProp, [key], defaultValue);
export const getAxisProperties = (chartModel) => chartModel.vizProp.axisProperties || [];
export const getAxes = (chartModel) => getAxisProperties(chartModel);
export const getXAxes = (chartModel) => {
    return getAxes(chartModel).filter(axis => axis.properties.axisType === AxisTypes.X);
};
export const getYAxes = (chartModel) => getAxes(chartModel).filter(axis => axis.properties.axisType === AxisTypes.Y);
export const getAxisPropertiesById = (vizProp, axisId) => { var _a; return (_a = _.find(vizProp === null || vizProp === void 0 ? void 0 : vizProp.axisProperties, ['id', axisId])) === null || _a === void 0 ? void 0 : _a.properties; };
export const getAxisForColumn = (chartModel, columnId) => {
    return getAxes(chartModel).find(axis => axis.properties.linkedColumns.some(col => col === columnId));
};
export const getAxisName = (chartModel, axisId, getTranslatedText) => {
    const axis = getAxisPropertiesById(chartModel.vizProp, axisId);
    if (!axis) {
        return '';
    }
    return (axis.name ||
        axis.linkedColumns
            .map(colId => {
            const column = getColumn(chartModel, colId);
            return getName(column);
        })
            .join(getTranslatedText('axisname.concatenator.symbol')));
};
export const isYAxisOpposite = (axis, idx) => {
    const isOpposite = axis.properties.isOpposite;
    return !_.isUndefined(isOpposite) ? isOpposite : idx > 0;
};
export const getAxisPosition = (chartModel, axisId) => {
    const axis = getAxisPropertiesById(chartModel.vizProp, axisId);
    if (axis) {
        return axis.isOpposite;
    }
    return null;
};
export const getAxisRange = (axisId, chartModel) => { var _a; return (_a = getAxisPropertiesById(chartModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.yAxisRange; };
export const isAxisYAxis = (chartModel, axisId) => { var _a; return ((_a = getAxisPropertiesById(chartModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.axisType) === AxisTypes.Y; };
const getLinkedColumnIdsForAxis = (chartModel, axisId) => { var _a; return ((_a = getAxisPropertiesById(chartModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.linkedColumns) || []; };
export const getLinkedColumnForAxis = (chartModel, axisId) => {
    const linkedColumns = getLinkedColumnIdsForAxis(chartModel, axisId);
    const allColumnsMap = getColumnsMap(chartModel);
    return _.map(linkedColumns, colId => allColumnsMap[colId]);
};
export const getGroupedColumnIdsForAxis = (chartModel, axisId) => { var _a; return ((_a = getAxisPropertiesById(chartModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.linkedColumns) || []; };
export const getChartProperties = (chartModel) => { var _a; return ((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp) === null || _a === void 0 ? void 0 : _a.chartProperties) || {}; };
export const getChartProperty = (chartModel, prop) => _.get(getChartProperties(chartModel), prop);
export const isResponsiveLayoutAuto = (chartModel) => {
    const responsiveLayoutPreference = getChartProperties(chartModel)
        .responsiveLayoutPreference;
    return (responsiveLayoutPreference === ChartResponsiveLayout.AutoOn ||
        responsiveLayoutPreference === ChartResponsiveLayout.AutoOff);
};
export const isResponsiveLayoutAutoOn = (chartModel) => {
    const responsiveLayoutPreference = getChartProperties(chartModel)
        .responsiveLayoutPreference;
    return responsiveLayoutPreference === ChartResponsiveLayout.AutoOn;
};
export const isResponsiveLayoutNull = (chartModel) => {
    const responsiveLayoutPreference = getChartProperties(chartModel)
        .responsiveLayoutPreference;
    return !responsiveLayoutPreference;
};
export const isResponsiveLayoutEnabled = (chartModel) => {
    const responsiveLayoutPreference = getChartProperties(chartModel)
        .responsiveLayoutPreference;
    return (responsiveLayoutPreference === ChartResponsiveLayout.UserPreferredOn ||
        responsiveLayoutPreference === ChartResponsiveLayout.AutoOn);
};
export const getVisibleSeriesIds = (chartModel) => getChartProperties(chartModel).visibleSeriesNames;
export const isSeriesPresentInVisibleSeries = (chartModel, dataModel) => {
    let visibleSeriesIds = getVisibleSeriesIds(chartModel);
    const seriesMap = getSeries(dataModel).map(series => {
        var _a;
        return {
            blinkSeriesId: series.blinkSeriesId.toLowerCase(),
            seriesName: (_a = series.name) === null || _a === void 0 ? void 0 : _a.toLowerCase(),
        };
    });
    visibleSeriesIds = visibleSeriesIds === null || visibleSeriesIds === void 0 ? void 0 : visibleSeriesIds.map(id => id.toLowerCase());
    if (visibleSeriesIds && visibleSeriesIds.length) {
        const isSeriesAvailable = seriesMap.some(seriesObj => {
            return (visibleSeriesIds.includes(seriesObj.blinkSeriesId) ||
                visibleSeriesIds.includes(seriesObj.seriesName));
        });
        return isSeriesAvailable;
    }
    return true;
};
export const getColorBySerieName = (serieColorMap, serieName) => {
    var _a;
    if (serieColorMap && serieColorMap.length && serieName) {
        return (_a = serieColorMap.find(colMap => colMap.serieName === serieName.toLowerCase())) === null || _a === void 0 ? void 0 : _a.color;
    }
    return null;
};
export const getShowAllLabels = (chartModel) => !!getChartProperties(chartModel).allLabels;
export const getShowRegressionLine = (chartModel) => !!getChartProperties(chartModel).showLinearRegressionLine;
export const isZoomedIn = (chartModel, iszoomedIn = false) => {
    if (iszoomedIn) {
        getChartProperties(chartModel).isZoomed = iszoomedIn;
    }
    return getChartProperties(chartModel).isZoomed;
};
export const getChartSpecificProperty = (chartModel) => getChartProperties(chartModel).chartSpecific;
export const getChartSpecificProperties = (chartModel) => {
    return getChartProperty(chartModel, CLIENT_STATE_KEYS.chartSpecific);
};
export function getPivotState(chartModel) {
    var _a;
    return (_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.pivotState;
}
export const getShowPivotSummaries = (chartModel) => { var _a; return !((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.hidePivotSummaries); };
export const getShowPivotSummaryPrior = (chartModel) => { var _a; return (_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.showPivotSummaryPrior; };
export const getPivotSummariesState = (chartModel) => { var _a; return (_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.pivotSummariesState; };
export const isMarkersEnabled = (chartModel) => { var _a; return !!((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.markersEnabled); };
export const getShowStackedLabels = (chartModel) => {
    return !!getChartProperties(chartModel).showStackedLabels;
};
export const isYAxisStackedAsPercent = (chartModel) => { var _a; return !!((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.isStackedAsPercent); };
export const getDataFieldArea = (chartModel) => {
    var _a, _b, _c;
    if (((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.dataFieldArea) &&
        ((_b = getChartSpecificProperty(chartModel)) === null || _b === void 0 ? void 0 : _b.dataFieldArea) !== '') {
        return (_c = getChartSpecificProperty(chartModel)) === null || _c === void 0 ? void 0 : _c.dataFieldArea;
    }
    return 'column';
};
export const isHeatMapOverlayed = (chartModel) => { var _a; return !!((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.isHeatmapOverlayed); };
export const getChartSpecificCustomProps = (chartModel) => {
    var _a, _b;
    try {
        if ((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.customProps) {
            return JSON.parse((_b = getChartSpecificProperty(chartModel)) === null || _b === void 0 ? void 0 : _b.customProps);
        }
    }
    catch (e) {
        logger.error('unable to parse ChartSpecificCustomProps', e);
        return undefined;
    }
    return undefined;
};
export const getAxisCustomProps = (vizProp, axisId) => {
    var _a, _b;
    try {
        if ((_a = getAxisPropertiesById(vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.customProps) {
            return JSON.parse((_b = getAxisPropertiesById(vizProp, axisId)) === null || _b === void 0 ? void 0 : _b.customProps);
        }
    }
    catch (e) {
        logger.error('unable to parse AxisCustomProps', e);
        return undefined;
    }
    return undefined;
};
export const getHighchartCustomisationSettings = (chartModel) => { var _a; return (_a = getChartSpecificCustomProps(chartModel)) === null || _a === void 0 ? void 0 : _a.advancedProperties; };
export const getAxisAdvancedPropertiesById = (vizProp, axisId) => { var _a; return (_a = getAxisCustomProps(vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.advancedProperties; };
export const isAllowedCustomColorPallete = (chartModel) => {
    if (!_.isUndefined(getChartSpecificCustomProps(chartModel)) &&
        !isComparisionEnabled(chartModel) &&
        (!isShowAnomaliesSupported(chartModel) ||
            !isShowAnomaliesEnabled(chartModel)) &&
        !isShowBoundsEnabled(chartModel)) {
        return false;
    }
    return true;
};
export const getCustomColorSelectorArray = (chartModel) => _.get(chartModel.vizProp, 'customColorSelectorArray', []);
export const getColumnProperties = (vizModel) => { var _a; return ((_a = vizModel.vizProp) === null || _a === void 0 ? void 0 : _a.columnProperties) || []; };
export const getColumnPropertiesById = (vizProp, columnId) => {
    const columnSpecificProperty = _.find(vizProp.columnProperties, [
        'columnId',
        columnId,
    ]);
    return columnSpecificProperty
        ? columnSpecificProperty.columnProperty
        : null;
};
export const getColumnCustomProps = (vizProp, columnId) => {
    var _a, _b;
    try {
        if ((_a = getColumnPropertiesById(vizProp, columnId)) === null || _a === void 0 ? void 0 : _a.customProps) {
            return JSON.parse((_b = getColumnPropertiesById(vizProp, columnId)) === null || _b === void 0 ? void 0 : _b.customProps);
        }
    }
    catch (e) {
        logger.error('unable to parse ColumnCustomProps', e);
        return undefined;
    }
    return undefined;
};
export const getColumnAdvancedPropertiesById = (vizProp, columnId) => { var _a; return (_a = getColumnCustomProps(vizProp, columnId)) === null || _a === void 0 ? void 0 : _a.advancedProperties; };
export const getAnswerColumnProperties = (chartModel, colId) => {
    var _a;
    const answerColumn = getColumn(chartModel, colId);
    if (answerColumn) {
        return ((_a = answerColumn.columnProps) === null || _a === void 0 ? void 0 : _a.columnProperties) || {};
    }
    return {};
};
export const hasConditionalFormattingForColumn = (vizModel, col) => {
    let columnProperty;
    if (vizModel.__typename === 'HeadlineViz') {
        columnProperty = vizModel.vizProp.columnProperty;
    }
    else {
        columnProperty = getColumnPropertiesById(vizModel.vizProp, col.id);
    }
    return (!!(columnProperty === null || columnProperty === void 0 ? void 0 : columnProperty.conditionalFormatting) &&
        !_.isEmpty(columnProperty === null || columnProperty === void 0 ? void 0 : columnProperty.conditionalFormatting.rows));
};
export const getAllColumnsInAxisConfig = (axisConfig) => {
    const vizColumns = [];
    for (const key in axisConfig) {
        if (key !== '__typename') {
            const axis = axisConfig[key];
            const value = Array.isArray(axis)
                ? axis
                : !_.isNil(axis)
                    ? [axis]
                    : [];
            Array.prototype.push.apply(vizColumns, value);
        }
    }
    return vizColumns;
};
export const getAllConditionalFormattingRules = (vizModel) => {
    var _a, _b;
    if (vizModel.__typename === 'HeadlineViz') {
        const columnId = vizModel.headlineColumn.id;
        return {
            [columnId]: (_b = (_a = vizModel.vizProp.columnProperty) === null || _a === void 0 ? void 0 : _a.conditionalFormatting) === null || _b === void 0 ? void 0 : _b.rows,
        };
    }
    const metrics = {};
    let columnsInAxisConfig = null;
    if (vizModel.__typename === 'ChartViz') {
        columnsInAxisConfig = new Set(getAllColumnsInAxisConfig(vizModel.config.axisConfig[0]).map(col => col.id));
    }
    vizModel.columns.forEach((column) => {
        const answerColumn = column.column;
        if ((!columnsInAxisConfig ||
            columnsInAxisConfig.has(answerColumn.id)) &&
            hasConditionalFormattingForColumn(vizModel, answerColumn)) {
            const columnProperty = getColumnPropertiesById(vizModel.vizProp, answerColumn.id);
            metrics[answerColumn.id] =
                columnProperty.conditionalFormatting.rows;
        }
    });
    return metrics;
};
export const getAllConditionalFormattingRulesForPlotlines = (vizModel, columnId) => {
    var _a, _b;
    const answerColumn = getColumn(vizModel, columnId);
    const isColumnDateFamily = supportsDateBucketizationChange(answerColumn);
    if (isColumnDateFamily) {
        return [];
    }
    let metrics = [];
    const columnProperty = getColumnPropertiesById(vizModel.vizProp, columnId);
    if ((_b = (_a = columnProperty === null || columnProperty === void 0 ? void 0 : columnProperty.conditionalFormatting) === null || _a === void 0 ? void 0 : _a.rows) === null || _b === void 0 ? void 0 : _b.length) {
        metrics = metrics.concat(columnProperty.conditionalFormatting.rows);
    }
    const filteredMetrics = metrics.filter((metric) => metric.comparisonType !==
        ConditionalFormattingComparisonTypes.ColumnBased &&
        (!metric.lhsColumnId || columnId === metric.lhsColumnId));
    return getEnvFlags().isEnhancedCfEnabled
        ? filteredMetrics
        : filteredMetrics.filter((metrics) => metrics.comparisonType !==
            ConditionalFormattingComparisonTypes.ParameterBased);
};
export const getSeriesColorsMap = (chartModel) => {
    const seriesColors = _.filter(chartModel.vizProp.seriesColors, serie => {
        return !_.isEmpty(serie.serieName) && !_.isEmpty(serie.color);
    });
    const systemSeriesColors = _.filter(chartModel.vizProp.systemSeriesColors, serie => {
        return !_.isEmpty(serie.serieName) && !_.isEmpty(serie.color);
    });
    const colorMap = _.cloneDeep(_.unionBy(seriesColors, systemSeriesColors, 'serieName'));
    const serieNames = _.map(colorMap, 'serieName');
    return _.filter(colorMap, ({ serieName, color }, key, obj) => {
        if (serieName !== serieName.toLowerCase()) {
            if (!serieNames.includes(serieName.toLowerCase())) {
                obj[key].serieName = serieName.toLowerCase();
            }
            else {
                return false;
            }
        }
        return true;
    });
};
export const getMultiColorSeriesColors = (chartModel) => {
    const { multiColorSeriesColors, systemMultiColorSeriesColors, } = chartModel.vizProp;
    if (!systemMultiColorSeriesColors)
        return _.cloneDeep(multiColorSeriesColors) || [];
    const combinedMultiColorSeriesColors = _.unionBy(_.cloneDeep(multiColorSeriesColors), _.cloneDeep(systemMultiColorSeriesColors), 'serieName');
    systemMultiColorSeriesColors.forEach(systemColor => {
        var _a;
        const existingColorMap = (_a = combinedMultiColorSeriesColors.find(colMap => colMap.serieName === systemColor.serieName)) === null || _a === void 0 ? void 0 : _a.colorMap;
        if (existingColorMap) {
            systemColor.colorMap.forEach(colorMapItem => {
                if (!existingColorMap.some(colorMap => colorMap.serieName === colorMapItem.serieName)) {
                    existingColorMap.push(colorMapItem);
                }
            });
        }
    });
    return combinedMultiColorSeriesColors;
};
export const getColorScaleOfMultiColorSeriesColors = (multiColorSeriesMap, serieName, columnHash) => {
    var _a, _b, _c;
    if (multiColorSeriesMap && serieName && columnHash) {
        return (_c = (_b = (_a = multiColorSeriesMap
            .find(colMap => colMap.serieName === serieName.toLowerCase())) === null || _a === void 0 ? void 0 : _a.colorMap) === null || _b === void 0 ? void 0 : _b.find(serieMap => (serieMap === null || serieMap === void 0 ? void 0 : serieMap.serieName) === columnHash.toLowerCase())) === null || _c === void 0 ? void 0 : _c.color;
    }
    return [];
};
export const getUpdatedSeriesColor = (serieColorMap, serieId, color) => {
    const filteredColor = serieColorMap.filter(serieMap => serieMap.serieName !== serieId.toLowerCase());
    return [...filteredColor, { color, serieName: serieId.toLowerCase() }];
};
export const setColorScaleOfMultiColorSeriesColors = (multiColorSeriesColors, serieId, xColumnHash, colorScale) => {
    let seriesColorMap = multiColorSeriesColors.find(colMap => (colMap === null || colMap === void 0 ? void 0 : colMap.serieName) === serieId.toLowerCase());
    if (!seriesColorMap) {
        seriesColorMap = { serieName: serieId.toLowerCase(), colorMap: [] };
    }
    const filteredMultiColorSeriesMap = multiColorSeriesColors.filter(serieMap => serieMap.serieName !== serieId.toLowerCase());
    const filteredColorMap = seriesColorMap.colorMap.filter(colHash => (colHash === null || colHash === void 0 ? void 0 : colHash.serieName) !== xColumnHash.toLowerCase());
    const newSeriesColorMap = { ...seriesColorMap };
    newSeriesColorMap.colorMap = [
        ...filteredColorMap,
        { serieName: xColumnHash.toLowerCase(), color: colorScale },
    ];
    return [...filteredMultiColorSeriesMap, newSeriesColorMap];
};
export const getMapViewport = (chartModel) => getChartProperties(chartModel).mapviewport;
export const getQueriesFromChartModel = (chartModel) => {
    return chartModel.config.axisConfig;
};
export const parseVisualAxisConfigFromQueryDefinition = (chartModel) => {
    const axisConfigs = translateAxisConfigWithMeasureNamesValues(chartModel);
    const visualAxisConfig = {};
    _.reduce(axisConfigs, (vizAxisConfig, axisConfig) => {
        Object.keys(axisConfig).forEach(key => {
            if (Array.isArray(axisConfig[key])) {
                vizAxisConfig[key] = _.uniqBy(_.concat([], vizAxisConfig[key] ? vizAxisConfig[key] : [], axisConfig[key] ? axisConfig[key] : []), 'id');
            }
            else {
                vizAxisConfig[key] = axisConfig[key]
                    ? axisConfig[key]
                    : null;
            }
        });
        return vizAxisConfig;
    }, visualAxisConfig);
    return visualAxisConfig;
};
export const parseQueryDefinitionFromVisualAxisConfig = (chartType, visualAxisConfig, chartModel) => {
    let axisConfigs = [visualAxisConfig];
    if (isMultiQueryChart(chartType)) {
        axisConfigs = getQueryDefinitions(chartType, visualAxisConfig, chartModel);
    }
    axisConfigs = translateQueryDefinitionWithMeasureNamesValues(axisConfigs, chartModel);
    return axisConfigs;
};
export const getAxisConfig = (chartModel) => {
    var _a;
    if (isMultiQueryChart(getChartType(chartModel))) {
        return parseVisualAxisConfigFromQueryDefinition(chartModel);
    }
    return _.first((_a = chartModel === null || chartModel === void 0 ? void 0 : chartModel.config) === null || _a === void 0 ? void 0 : _a.axisConfig) || {};
};
export const getUseTreeLayout = (chartModel) => {
    var _a, _b;
    if (containsMeasureNamesOrMeasureValuesColumns(getAxisConfig(chartModel)) &&
        ((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.useFlatLayout) === null) {
        return false;
    }
    return !((_b = getChartSpecificProperty(chartModel)) === null || _b === void 0 ? void 0 : _b.useFlatLayout);
};
export const getVisualizedColumns = (chartModel) => {
    const axisConfig = getAxisConfig(chartModel);
    return getAllColumnsInAxisConfig(axisConfig);
};
export const getVisualizedMeasureColumns = (chartModel) => {
    const visualizedColumns = getVisualizedColumns(chartModel);
    return visualizedColumns.filter(column => isEffectivelyNumeric(column) && !isGroupingColumn(column));
};
export const getSuggestedAxisConfig = (chartModel, requiredChartType) => {
    const suggestedConfigs = chartModel.suggestedConfig || [];
    if (!suggestedConfigs.length) {
        return undefined;
    }
    const chartType = requiredChartType || getChartType(chartModel);
    const suggestedConfig = suggestedConfigs.filter(config => {
        return config.chartType === chartType;
    })[0];
    if (!suggestedConfig) {
        return undefined;
    }
    return _.first(suggestedConfig.axisConfig) || {};
};
export const isChartConfigurationLocked = (chartViz) => {
    var _a;
    return !!((_a = chartViz === null || chartViz === void 0 ? void 0 : chartViz.config) === null || _a === void 0 ? void 0 : _a.isLocked);
};
export const getAxisColumns = (chartModel, axis, defaultValue = []) => _.get(getAxisConfig(chartModel), axis, defaultValue);
export const getCustomChartAxisColumn = (customChartConfig, axis) => {
    let customChartAxisColumns = [];
    customChartConfig.forEach(config => {
        if (config.dimensions && Array.isArray(config.dimensions)) {
            config.dimensions.forEach(dimension => {
                if (dimension.key === axis) {
                    customChartAxisColumns = customChartAxisColumns.concat(dimension.columns);
                }
            });
        }
    });
    return customChartAxisColumns;
};
export const getXAxisColumns = (chartModel) => getAxisColumns(chartModel, 'x');
export const getYAxisColumns = (chartModel) => getAxisColumns(chartModel, 'y');
export const getCustomChartXAxisColumns = (chartModel) => getCustomChartAxisColumn(chartModel.config.customChartConfig, 'x');
export const getCustomChartYAxisColumns = (chartModel) => getCustomChartAxisColumn(chartModel.config.customChartConfig, 'y');
export const isYAxisColumn = (chartModel, columnId) => _.indexOf(getYAxisColumns(chartModel).map(col => getId(col)), columnId) >= 0;
export const isXAxisColumn = (chartModel, columnId) => _.indexOf(getXAxisColumns(chartModel).map(col => getId(col)), columnId) >= 0;
export const isFirstYAxisColumn = (chartModel, columnId) => {
    return (!!_.first(getYAxisColumns(chartModel)) &&
        getId(_.first(getYAxisColumns(chartModel))) === columnId);
};
export const getYAxisColumn = (chartModel, distinctNameIdentifier) => {
    const distinctYAxisColumnNames = getDistinctNamesForColumns(getYAxisColumns(chartModel));
    const index = _.indexOf(distinctYAxisColumnNames, distinctNameIdentifier);
    return getYAxisColumns(chartModel)[index];
};
export const getYAxisColumnById = (chartModel, yAxisColumnId) => {
    return _.find(getYAxisColumns(chartModel), col => col.id === yAxisColumnId);
};
export const getLegendColumns = (chartModel) => getAxisColumns(chartModel, 'color');
export const getRadialColumn = (chartModel) => _.get(getAxisConfig(chartModel), 'size', null);
export const getCategoryColumns = (chartModel) => getAxisColumns(chartModel, 'category');
export const getSortColumns = (chartModel) => getAxisColumns(chartModel, 'sort');
export const isXAxisMeasure = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    return xAxisColumns.length === 1 && isEffectivelyNumeric(xAxisColumns[0]);
};
export const isYAxisMeasure = (chartModel) => _.every(getYAxisColumns(chartModel), column => isEffectivelyNumeric(column));
export const isXAxisOrYAxisColumns = (answerColumn, chartViz) => {
    if (chartViz.config.chartType === ChartType.CustomChart) {
        const xAxisColumns = getCustomChartXAxisColumns(chartViz);
        const yAxisColumns = getCustomChartYAxisColumns(chartViz);
        return [...xAxisColumns, ...yAxisColumns].some(column => {
            return column === answerColumn.id;
        });
    }
    const xAxisColumns = getXAxisColumns(chartViz);
    const yAxisColumns = getYAxisColumns(chartViz);
    return [...xAxisColumns, ...yAxisColumns].some(column => {
        return column.id === answerColumn.id;
    });
};
export const isAllowConditionalFormatting = (chartViz, chartType, answerColumn) => {
    const chartConfigOptions = configOptions(chartType, chartViz);
    if (!chartConfigOptions.allowedConfigurations.conditionalFormatting)
        return false;
    let isFormattingApplicableOnColumn = (isNumeric(answerColumn) && !isMeasure(answerColumn)) ||
        isMeasure(answerColumn) ||
        (isAttribute(answerColumn) &&
            chartConfigOptions.allowedConfigurations
                .conditionalFormatEditorParams
                .isConditionalFormattingAllowedOnAttributeColumn);
    const isGradientColoringEnabled = getEnvFlags().isEnhancedCfEnabled &&
        getEnvFlags().isGradientColoringEnabled;
    const isConditionalFormattingAllowedOnRadialColumn = isGradientColoringEnabled &&
        chartConfigOptions.allowedConfigurations.conditionalFormatEditorParams
            .isConditionalFormattingAllowedOnRadialColumn;
    if (chartType === ChartType.PivotTable) {
        isFormattingApplicableOnColumn =
            isFormattingApplicableOnColumn &&
                isYAxisColumn(chartViz, answerColumn.id) &&
                !isDateColumn(answerColumn);
    }
    return (isFormattingApplicableOnColumn &&
        (isXAxisOrYAxisColumns(answerColumn, chartViz) ||
            !!isConditionalFormattingAllowedOnRadialColumn));
};
export const isConditionalFormattingAppliedOnViz = (chartViz) => {
    return !!_.find((chartViz === null || chartViz === void 0 ? void 0 : chartViz.columns) || [], (viz) => hasConditionalFormattingForColumn(chartViz, viz.column));
};
export const getXAxisColumnsHash = (chartModel) => getXAxisColumns(chartModel)
    .map(col => {
    return getId(col);
})
    .join(',');
export const getStackedAsPercentChartFormat = (chartModel) => { var _a; return (_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.stackedAsPercentFormat; };
export const getUserDefinedFormattingForAxis = (chartModel, axisId) => {
    var _a;
    return (_a = getAxisPropertiesById(chartModel.vizProp, axisId)) === null || _a === void 0 ? void 0 : _a.format;
};
const _getFormattingForAxis = (chartModel, axisId) => {
    const linkedVizColumns = getLinkedColumnForAxis(chartModel, axisId);
    const columnToConsider = _.find(linkedVizColumns, col => isColumnValidForNumberFormatting(col));
    if (!columnToConsider) {
        return undefined;
    }
    const userDefinedAxisFormat = getUserDefinedFormattingForAxis(chartModel, axisId);
    if (userDefinedAxisFormat) {
        return userDefinedAxisFormat;
    }
    const formatConfig = getColumnFormatPattern(columnToConsider);
    if (linkedVizColumns.length > 1) {
        if (hasUnit(formatConfig) && !isDefaultNumberFormatUnitNone()) {
            setUnit(formatConfig, Unit.Auto);
        }
    }
    return formatConfig;
};
const _getFormattingForCumulativeChartAxis = (chartModel, axisId) => {
    const formatConfig = _getFormattingForAxis(chartModel, axisId);
    if (!formatConfig) {
        return undefined;
    }
    return formatConfig;
};
const _getFormattingForStackedChartAxis = (chartModel, axisId) => {
    if (getYAxes(chartModel)[0].id === axisId &&
        isYAxisStackedAsPercent(chartModel) &&
        isAxisYAxis(chartModel, axisId)) {
        const formatConfig = getStackedAsPercentChartFormat(chartModel);
        if (formatConfig) {
            return formatConfig;
        }
    }
    return _getFormattingForAxis(chartModel, axisId);
};
export const getFormattingForAxis = (chartModel, axisId) => {
    if (isStackedChart(getChartType(chartModel))) {
        return _getFormattingForStackedChartAxis(chartModel, axisId);
    }
    const chartType = getChartType(chartModel);
    if (configOptions(chartType, chartModel).plotsCumulativeMeasure &&
        isAxisYAxis(chartModel, axisId)) {
        return _getFormattingForCumulativeChartAxis(chartModel, axisId);
    }
    return _getFormattingForAxis(chartModel, axisId);
};
export const getPivotSummaryMode = (chartModel) => { var _a; return ((_a = getChartSpecificProperty(chartModel)) === null || _a === void 0 ? void 0 : _a.summaryMode) || NULL_SUMMARY_MODE; };
export const getUserDefinedFormattingForColumn = (chartModel, colId) => { var _a; return (_a = getAnswerColumnProperties(chartModel, colId)) === null || _a === void 0 ? void 0 : _a.format; };
const getPivotSummaryModeFormat = (chartModel, colId) => {
    const userDefinedConfig = getUserDefinedFormattingForColumn(chartModel, colId);
    if (getPivotSummaryMode(chartModel) === NULL_SUMMARY_MODE) {
        if (userDefinedConfig) {
            return userDefinedConfig;
        }
        const column = getColumns(chartModel).find(col => {
            return getId(col) === colId;
        });
        return getColumnFormatPattern(column);
    }
    if (userDefinedConfig === null || userDefinedConfig === void 0 ? void 0 : userDefinedConfig.percentageFormatConfig) {
        return userDefinedConfig;
    }
    return DEFAULT_PERCENTAGE_FORMAT;
};
const getFormattingForPivotColumn = (chartModel, colId) => {
    if (getPivotSummaryMode(chartModel) === NULL_SUMMARY_MODE) {
        const userDefinedConfig = getUserDefinedFormattingForColumn(chartModel, colId);
        if (userDefinedConfig) {
            return userDefinedConfig;
        }
        const column = getColumns(chartModel).find(col => {
            return getId(col) === colId;
        });
        const formatPattern = getColumnFormatPattern(column);
        return formatPattern;
    }
    return (getPivotSummaryModeFormat(chartModel, colId) ||
        DEFAULT_PERCENTAGE_FORMAT);
};
export const formatCompositeAxisValue = (chartModel, values, dataModel) => {
    const effectiveIdToColumnMap = getEffectiveIdToColumnMap(chartModel);
    return values
        .map(valueObject => {
        const columnEffectiveId = valueObject.columnEffectiveId;
        const column = effectiveIdToColumnMap[columnEffectiveId];
        const formatPattern = getFormatPattern(column);
        const columnLocale = getLocale(column);
        if (!column) {
            logger.error('no column for column effective id', columnEffectiveId);
            return valueObject.value;
        }
        if (isColumnValidForNumberFormatting(column)) {
            const axis = getAxisForColumn(chartModel, getId(column));
            const formatConfig = getFormattingForAxis(chartModel, axis.id);
            return getValueFormattedByConfig(valueObject.value, formatConfig, { formatPattern, columnLocale });
        }
        const displayToCustomCalendarValueMap = dataModel.getDisplayCustomCalendarMapForColumn(column);
        return getDataFormatter(column)(valueObject.value, {
            noShorten: false,
            displayToCustomCalendarValueMap,
        });
    })
        .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
};
export const getStackLabelDecimalPrecision = (chartModel) => {
    var _a;
    return (_a = getDecimals(getStackedAsPercentChartFormat(chartModel))) !== null && _a !== void 0 ? _a : DEFAULT_DECIMAL_PRECISION;
};
export const getFormattingForColumn = (chartModel, colId) => {
    if (getChartType(chartModel) === ChartType.PivotTable) {
        return getFormattingForPivotColumn(chartModel, colId);
    }
    const userDefinedConfig = getUserDefinedFormattingForColumn(chartModel, colId);
    if (userDefinedConfig) {
        return userDefinedConfig;
    }
    const column = getColumns(chartModel).find(col => {
        return getId(col) === colId;
    });
    return getColumnFormatPattern(column);
};
export const getAllAxesConfig = (chartModel) => {
    return getAxes(chartModel).reduce((acc, val, key) => {
        acc[val.id] = {
            ...val.properties,
            formatting: getFormattingForAxis(chartModel, val.id),
        };
        return acc;
    }, {});
};
export const formatCompositeColumnValue = (chartModel, values, dataModel) => {
    const effectiveIdToColumnMap = getEffectiveIdToColumnMap(chartModel);
    return values
        .map(valueObject => {
        const columnEffectiveId = valueObject.columnEffectiveId;
        const column = effectiveIdToColumnMap[columnEffectiveId];
        const formatPattern = getFormatPattern(column);
        const columnLocale = getLocale(column);
        if (!column) {
            logger.error('no column for column effective id', columnEffectiveId);
            return valueObject.value;
        }
        const formatConfig = getFormattingForColumn(chartModel, getId(column));
        if (formatConfig && isColumnValidForNumberFormatting(column)) {
            return getValueFormattedByConfig(valueObject.value, formatConfig, { formatPattern, columnLocale });
        }
        const displayToCustomCalendarValueMap = dataModel.getDisplayCustomCalendarMapForColumn(column);
        return getDataFormatter(column)(valueObject.value, {
            noShorten: false,
            displayToCustomCalendarValueMap,
        });
    })
        .join(formattingConstants.COMPOSITE_COLUMN_VALUES_JOIN_KEY);
};
const getFormattedValueForNumericAxis = (value, column, allColumnValuesOnAxis) => {
    var _a;
    const isDouble = isDoubleColumn(column);
    let nDecimal;
    let noShorten = false;
    const formatPattern = getFormatPattern(column);
    const columnLocale = getLocale(column);
    if (isColumnValidForNumberFormatting(column) && typeof value === 'number') {
        const userDefinedConfig = (_a = column.columnProps.columnProperties) === null || _a === void 0 ? void 0 : _a.format;
        if (userDefinedConfig) {
            return getValueFormattedByConfig(value, userDefinedConfig, {
                formatPattern,
                columnLocale,
            });
        }
    }
    if (isDouble) {
        if (allColumnValuesOnAxis && allColumnValuesOnAxis.length > 1) {
            nDecimal = getMinDifferentiatingPrecision(allColumnValuesOnAxis, blinkConstants.decimalPrecision.MAX);
            if (isEffectivelyPercent(column)) {
                nDecimal = Math.max(DEFAULT_DECIMAL_PRECISION, nDecimal - 2);
            }
        }
        else {
            nDecimal = blinkConstants.decimalPrecision.MIN;
        }
        noShorten = nDecimal > 0;
    }
    return getDataFormatter(column)(value, {
        noShorten,
        nDecimal,
    });
};
const getFormattingFromChartVizProps = (chartViz) => {
    const chartSpecificProperties = getChartSpecificProperty(chartViz);
    const isStackChart = isStackedChart(chartViz.config.chartType);
    const isStackPercentChecked = isYAxisStackedAsPercent(chartViz);
    if (isStackChart && isStackPercentChecked) {
        return chartSpecificProperties.stackedAsPercentFormat;
    }
    return null;
};
export const getFormattingFromChart = (chartViz, currentColumn) => {
    if (isYAxisColumn(chartViz, getId(currentColumn))) {
        const formatConfig = getFormattingFromChartVizProps(chartViz);
        if (formatConfig) {
            return formatConfig;
        }
    }
    return getFormattingFromColumn(currentColumn);
};
export const getGeoObjects = (chartModel) => [];
export const getSortIndex = (chartModel, col) => {
    const sortOrder = getSortOrderByColId(chartModel, col.id);
    if (_.isNil(chartModel.sortInfo)) {
        logger.error('Sort info is missing in the chart object');
    }
    if (sortOrder) {
        return _.indexOf(chartModel.sortInfo, sortOrder);
    }
    return null;
};
export const isSorted = (chartModel, col) => {
    const sortOrder = getSortOrderByColId(chartModel, col.id);
    return !_.isNil(sortOrder);
};
export const isUserSorted = (chartModel, col) => {
    return isSorted(chartModel, col);
};
export const xAxisColumnHasCustomSort = (chartModel, xAxisColumns) => {
    return xAxisColumns.some(xAxisColumn => {
        var _a;
        return (isSorted(chartModel, xAxisColumn) &&
            ((_a = xAxisColumn.customOrder) === null || _a === void 0 ? void 0 : _a.length) > 0);
    });
};
export const isCustomOrderDefined = (column) => { var _a; return !!((_a = column.customOrder) === null || _a === void 0 ? void 0 : _a.length); };
export const getSortFormatFromColumn = (column) => {
    var _a;
    return (((_a = column.columnProps.columnProperties) === null || _a === void 0 ? void 0 : _a.sortConfig) ||
        (isCustomOrderDefined(column)
            ? ColumnSortConfigOptions.Custom
            : ColumnSortConfigOptions.Default));
};
export const isSortKey = (chartModel, col) => isSorted(chartModel, col);
export const isAscendingSort = (chartModel, col) => {
    const sortOrder = getSortOrderByColId(chartModel, col.id);
    return sortOrder && sortOrder.sortAscending;
};
export const isDescendingSort = (chartModel, col) => {
    const sortOrder = getSortOrderByColId(chartModel, col.id);
    return sortOrder && !sortOrder.sortAscending;
};
export const isPrimarySortOnXAxisColumns = (chartModel, xAxisColumns, yAxisColumns) => {
    let maxXAxisColSortIndex = Number.NEGATIVE_INFINITY;
    for (let i = 0; i < xAxisColumns.length; i++) {
        const xAxisColumn = xAxisColumns[i];
        if (!isSorted(chartModel, xAxisColumn)) {
            return false;
        }
        maxXAxisColSortIndex = Math.max(maxXAxisColSortIndex, getSortIndex(chartModel, xAxisColumn));
    }
    return !_.every(yAxisColumns, function (yAxisColumn) {
        return (isSorted(chartModel, yAxisColumn) &&
            getSortIndex(chartModel, yAxisColumn) < maxXAxisColSortIndex);
    });
};
export const isDateColumnChart = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    const yAxisColumns = getYAxisColumns(chartModel);
    return (xAxisColumns.length === 1 &&
        isDateFamilyColumn(xAxisColumns[0]) &&
        isPrimarySortOnXAxisColumns(chartModel, xAxisColumns, yAxisColumns));
};
export const columnsMakeTimeSeries = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    const yAxisColumns = getYAxisColumns(chartModel);
    return (xAxisColumns.length === 1 &&
        isDateColumn(xAxisColumns[0]) &&
        isCalendarGregorianOrFiscal(xAxisColumns[0]) &&
        isPrimarySortOnXAxisColumns(chartModel, xAxisColumns, yAxisColumns));
};
export const isTimeSeries = (chartModel) => columnsMakeTimeSeries(chartModel);
export const isCustomCalendarTimeSeries = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    const yAxisColumns = getYAxisColumns(chartModel);
    return (xAxisColumns.length === 1 &&
        isDateColumn(xAxisColumns[0]) &&
        hasCustomCalendar(xAxisColumns[0]) &&
        isPrimarySortOnXAxisColumns(chartModel, xAxisColumns, yAxisColumns));
};
export const isXAxisOrdinalBased = (chartModel) => {
    const chartType = getChartType(chartModel);
    if (useOrdinalXAxis(chartType)) {
        return true;
    }
    const useMeasureAsOrdinal = useMeasureOnXAxisAsOrdinal(chartType);
    return !((isXAxisMeasure(chartModel) && !useMeasureAsOrdinal) ||
        isTimeSeries(chartModel));
};
export function areAllTimeSeriesColumns(columns) {
    return columns.every(column => {
        return isDateColumn(column) || isTimeColumn(column);
    });
}
export const isXAxisDate = (chartModel) => {
    const xAxisColumns = getXAxisColumns(chartModel);
    return xAxisColumns.length === 1 && isDateColumn(xAxisColumns[0]);
};
export const isYAxisDateOrTime = (chartModel) => _.every(getYAxisColumns(chartModel), column => isDateColumn(column) || isTimeColumn(column));
export const isYAxisOrdinalBased = (chartModel) => {
    const chartType = getChartType(chartModel);
    const useMeasureAsOrdinal = useMeasureOnYAxisAsOrdinal(chartType);
    return ((!isYAxisMeasure(chartModel) && !isYAxisDateOrTime(chartModel)) ||
        useMeasureAsOrdinal);
};
export const getMaxDataSizeForHighCardinalityChart = () => getMaxDataSizeForHighCardinalityCharts();
export const batchSizeValid = (batchSize) => {
    if (batchSize <= getMaxDataSizeForHighCardinalityCharts()) {
        return true;
    }
    return false;
};
export const getBatchSize = (chartModel, defaultBatchSize, chartType) => {
    let batchSize;
    if (doesSupportHighCardinalityData(chartType)) {
        const highCardinalityBatchSize = getChartProperties(chartModel).dataSize ||
            getDefaultDataSizeForHighCardinalityCharts();
        if (_.isFinite(highCardinalityBatchSize) &&
            highCardinalityBatchSize >= 0) {
            return highCardinalityBatchSize;
        }
    }
    let chartSpecificBatchSize;
    if (isGeoChartType(chartType)) {
        return getGeoDataBatchSize();
    }
    if (!_.isNaN((chartSpecificBatchSize = parseInt(configOptions(chartType, null).batchSize)))) {
        return chartSpecificBatchSize;
    }
    return defaultBatchSize || blinkConstants.DEFAULT_DATA_BATCH_SIZE;
};
export const canShowRegressionLine = (chartModel) => configOptions(getChartType(chartModel), null).allowedConfigurations
    .regressionLine;
export const hasAnySortedMeasureNotOnAxis = (chartModel) => {
    const radialColumn = getRadialColumn(chartModel);
    const columnsInAxis = _.concat([], getXAxisColumns(chartModel), getYAxisColumns(chartModel), getLegendColumns(chartModel), radialColumn ? [radialColumn] : []);
    const sortedMeasureColsNotOnAxis = getMeasureColumns(chartModel).filter(col => isSorted(chartModel, col) &&
        !_.find(columnsInAxis, axisCol => getId(col) === getId(axisCol)));
    return !_.isEmpty(sortedMeasureColsNotOnAxis);
};
export const yAxisColumnHasSorting = (chartModel) => getYAxisColumns(chartModel).some(column => isUserSorted(chartModel, column));
export const hasAnySortedMeasure = (chartModel) => getColumns(chartModel).some(column => isMeasure(column) && isSorted(chartModel, column));
export const xAxisColumnHasSorting = (chartModel) => _.some(getXAxisColumns(chartModel), column => isUserSorted(chartModel, column));
export const isColorRotationDisabled = (chartModel) => getEnvFlags().disableColorRotation;
export const getLabelForNumericAxis = (chartModel, value, axisId, allColumnValuesOnAxis, config) => {
    const column = getColumn(chartModel, _.first(getGroupedColumnIdsForAxis(chartModel, axisId)));
    const formatPattern = getFormatPattern(column);
    const columnLocale = getLocale(column);
    const format = getFormattingForAxis(chartModel, axisId);
    if (format && isColumnValidForNumberFormatting(column)) {
        const formattedValue = getValueFormattedByConfig(value, format, {
            formatPattern,
            columnLocale,
        });
        return formattedValue;
    }
    return getFormattedValueForNumericAxis(value, column, allColumnValuesOnAxis);
};
export const shouldShowDataLabels = (chartModel, dataModel, maxLabelCount, defaultBatchSize) => {
    if (!dataModel || !dataModel.getDataArray()) {
        return true;
    }
    const data = dataModel.getDataArray();
    const totalRowCount = _.first(data).getData().length;
    if (!isResponsiveLayoutEnabled(chartModel)) {
        return true;
    }
    if (Math.min(getBatchSize(chartModel, defaultBatchSize, getChartType(chartModel)), totalRowCount) > maxLabelCount) {
        return false;
    }
    return true;
};
export const getShowDataLabels = (chartModel, columnId) => {
    var _a, _b;
    const shouldShowDataLabel = shouldShowDataLabelToggleForColumn(chartModel, columnId);
    const shouldShowDetailLabel = shouldShowDetailLabelToggleForColumn(chartModel, columnId);
    const chartConfigOptions = configOptions(getChartType(chartModel), chartModel);
    const defaultDataLabelValue = chartConfigOptions.dataLabelsEnabledByDefault;
    let dataLabelcolumnId = columnId;
    if (chartModel.config.chartType === ChartType.Heatmap) {
        const toggledColumn = getRadialColumn(chartModel);
        dataLabelcolumnId = getId(toggledColumn);
    }
    const showDataLabel = (_b = (_a = getColumnPropertiesById(chartModel === null || chartModel === void 0 ? void 0 : chartModel.vizProp, dataLabelcolumnId)) === null || _a === void 0 ? void 0 : _a.dataLabels) !== null && _b !== void 0 ? _b : defaultDataLabelValue;
    return (shouldShowDataLabel || shouldShowDetailLabel) && showDataLabel;
};
export const isHighchartsProvider = (vizModel) => {
    return (getChartProvider(getChartType(vizModel), !!vizModel.cortexData) ===
        chartProviders.HIGHCHART);
};
export const hasMeasuresInPivotValues = (chartModel) => getYAxisColumns(chartModel).some(col => isEffectivelyNumeric(col));
export const hasAttributesInPivotValues = (chartModel) => getYAxisColumns(chartModel).some(col => isAttribute(col));
export const hasOnlyAttributesInPivotValues = (chartModel) => getYAxisColumns(chartModel).every(col => isAttribute(col));
export const isHandleMissingValuesConfigurable = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.advanced.display
        .handleMissingValues ||
        !isXAxisDate(chartModel)) {
        return false;
    }
    if (chartType === ChartType.LineStackedColumn ||
        chartType === ChartType.LineColumn) {
        if (isFirstYAxisColumn(chartModel, columnId)) {
            return false;
        }
    }
    return isYAxisColumn(chartModel, columnId);
};
export const isShowNullAsZeroSupported = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.advanced.display
        .showNullAsZero ||
        !isXAxisDate(chartModel)) {
        return false;
    }
    if (chartType === ChartType.LineStackedColumn ||
        chartType === ChartType.LineColumn) {
        if (isFirstYAxisColumn(chartModel, columnId)) {
            return true;
        }
        return false;
    }
    return isYAxisColumn(chartModel, columnId);
};
export const isExcludeNullValuesSupported = (chartModel, columnId) => {
    const chartType = getChartType(chartModel);
    const chartConfigOptions = configOptions(chartType, chartModel);
    if (!chartConfigOptions.allowedConfigurations.excludeNullValues) {
        return false;
    }
    return isXAxisColumn(chartModel, columnId) && isXAxisDate(chartModel);
};
export const getHandleMissingValues = (chartModel, colId) => {
    var _a;
    const isHandleMissingValuesConfigurableForColumn = isHandleMissingValuesConfigurable(chartModel, colId) ||
        isShowNullAsZeroSupported(chartModel, colId);
    return isHandleMissingValuesConfigurableForColumn
        ? (_a = getColumnPropertiesById(chartModel.vizProp, colId)) === null || _a === void 0 ? void 0 : _a.handleMissingValues
        : undefined;
};
export const isExcludeNullValuesSelected = (chartModel, colId) => {
    var _a;
    const excludeNullValues = (_a = getColumnPropertiesById(chartModel.vizProp, colId)) === null || _a === void 0 ? void 0 : _a.excludeNullValues;
    return excludeNullValues !== null && excludeNullValues !== void 0 ? excludeNullValues : true;
};
export const isConnectNullsEnabled = (chartModel, colId) => {
    const handleMissingValuesValue = getHandleMissingValues(chartModel, colId);
    return (_.isNil(handleMissingValuesValue) ||
        handleMissingValuesValue !== HandleMissingValues.ShowGap);
};
export const getColumnNames = (chartModel, columnIds) => {
    return columnIds === null || columnIds === void 0 ? void 0 : columnIds.map((columnId) => getName(getColumn(chartModel, columnId)));
};
export const getColorGradient = (chartModel, columnId) => {
    var _a;
    const colors = [];
    const range = [];
    const conditionalFormats = getConditionalFormats(chartModel.vizProp, columnId, 'ChartVizProps', chartModel);
    const conditionalFormatsForGradient = conditionalFormats.filter((metric) => metric.backgroundFormatType === BackgroundFormatTypes.Gradient);
    const gradientBackgroundAttrs = (_a = conditionalFormatsForGradient[0]) === null || _a === void 0 ? void 0 : _a.gradientBackgroundAttrs;
    if (!gradientBackgroundAttrs) {
        return {
            range: null,
            colors: null,
        };
    }
    if (gradientBackgroundAttrs.colors) {
        colors.push(...gradientBackgroundAttrs.colors);
    }
    if (gradientBackgroundAttrs.backgroundFormatRange) {
        range.push(...gradientBackgroundAttrs.backgroundFormatRange);
    }
    return {
        range: range.length ? range : null,
        colors: colors.length ? colors : null,
    };
};
export const getXAxisFilterdWithTooltipEnabled = (chartModel, columns) => {
    const { tooltipConfig = {} } = chartModel.vizProp;
    const { columnIds } = tooltipConfig;
    if (!_.isEmpty(columns)) {
        return columns.filter(item => columnIds.includes(item.id));
    }
    return columns;
};
export const getIsYAxisColInLineStackedChart = (chartModel, columnId) => {
    var _a, _b;
    const chartType = getChartType(chartModel);
    const isLineStackedChart = chartType === ChartType.LineStackedColumn;
    const stackedQueryIndex = 1;
    const yAxisColumnsPlottedAsSeries = (_b = (_a = chartModel.config.axisConfig) === null || _a === void 0 ? void 0 : _a[stackedQueryIndex]) === null || _b === void 0 ? void 0 : _b.y;
    const isYAxisCol = yAxisColumnsPlottedAsSeries === null || yAxisColumnsPlottedAsSeries === void 0 ? void 0 : yAxisColumnsPlottedAsSeries.some(col => col.id === columnId);
    return !!(isLineStackedChart && isYAxisCol);
};
//# sourceMappingURL=chart-model-utils.js.map